/**
 * TesterHome — the signed-in home of the UAT Tester Portal (route `/app`).
 *
 * Stories: epic-uat-portal-001-14 (introduced), epic-uat-portal-001-15 (home content).
 *
 * The signed-in landing is the REAL homepage of the portal, not a 3-link stub
 * ("you get just the 3 options and you aren't taken to the homepage" — Coby,
 * -15): a short welcome + quick entry-point cards, then the same substantive
 * content the public landing carries — the 3-step journey, the Door A/B/
 * Governance walkthroughs, and the capture how-to (reused from `LandingPage`).
 * A signed-in tester gets everything they need to actually test, with the
 * authed destinations (`/report`, `/board`) one click away.
 *
 * The persistent nav lives in `GatedLayout` (the shell wrapping every gated
 * page); this home repeats those destinations as cards because the post-login
 * screen is where a tester gets their bearings.
 *
 * Copy mirrors the plain/clear tester voice of LandingPage (awaits an optional
 * Menley voice-pass; not blocking).
 */

import { Link } from 'react-router-dom';
import { useTesterAuth } from '@/auth/testerAuthContext';
import { JourneySteps } from '@/features/landing/JourneySteps';
import { Walkthroughs } from '@/features/landing/Walkthroughs';
import { CaptureHowTo } from '@/features/landing/CaptureHowTo';

interface EntryPoint {
  to: string;
  testid: string;
  title: string;
  blurb: string;
}

const ENTRY_POINTS: EntryPoint[] = [
  {
    to: '/guide',
    testid: 'home-card-guide',
    title: 'How to test',
    blurb: 'Read the full walkthrough — what to try and how to capture it.',
  },
  {
    to: '/report',
    testid: 'home-card-report',
    title: 'Report a bug',
    blurb: 'Found something off? File it here — no Discord needed.',
  },
  {
    to: '/board',
    testid: 'home-card-board',
    title: 'Status board',
    blurb: 'See everything other testers have found and where it stands.',
  },
];

export function TesterHome() {
  const { account } = useTesterAuth();

  return (
    <main data-testid="tester-home">
      {/* Welcome + quick entry points to the authed destinations */}
      <section className="mx-auto w-full max-w-4xl px-6 pb-4 pt-12">
        <h1 className="mb-2 text-3xl font-bold" data-testid="home-heading">
          {account?.displayName ? `Welcome, ${account.displayName}.` : 'Welcome, tester.'}
        </h1>
        <p className="mb-8 text-muted-foreground" data-testid="home-intro">
          You&apos;re signed in. Read a walkthrough below, file what you find, or
          see what everyone else has found.
        </p>

        <div className="grid gap-4 sm:grid-cols-3">
          {ENTRY_POINTS.map((e) => (
            <Link
              key={e.to}
              to={e.to}
              data-testid={e.testid}
              className="flex flex-col gap-2 rounded border border-gray-200 p-5 hover:border-gray-400"
            >
              <span className="font-semibold">{e.title}</span>
              <span className="text-sm text-muted-foreground">{e.blurb}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* The real homepage content, reused from the public landing (-15) so a
          signed-in tester has everything to test, not just the links above. */}
      <JourneySteps />
      <Walkthroughs />
      <CaptureHowTo />
    </main>
  );
}
